import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, LockKey, User, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

// BILLING-IDENTITY-SEPARATION-001 (task section 10): resolves the
// one-time activation link emailed after a PUBLIC purchase. This screen
// reads `useAuth().user` ONLY to decide which UI to show for the
// "an account with this email already exists" case (log-in prompt vs a
// one-click "link to my account" confirmation) — it never auto-adopts
// that session, never renders it as if the purchase already belongs to
// it, and the backend independently re-verifies the caller's email
// against the purchase's checkout email before ever attaching anything.
export default function PurchaseActivate() {
  const { api, user, applyAuthSession } = useAuth();
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [status, setStatus] = useState('loading');
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [accountExists, setAccountExists] = useState(false);
  const [planId, setPlanId] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activatedPlan, setActivatedPlan] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('not_found');
      return;
    }
    api.get('/public/billing/activation-status', { params: { token } })
      .then(({ data }) => {
        setStatus(data.status);
        setMaskedEmail(data.masked_email || null);
        setAccountExists(Boolean(data.account_exists));
        setPlanId(data.plan_id || '');
      })
      .catch(() => setStatus('not_found'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitNewAccount = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/public/billing/activate', { token, name, password });
      applyAuthSession(data);
      setActivatedPlan(data?.user?.plan || planId);
      setStatus('activated');
    } catch (e) {
      setError(e.response?.data?.detail || t('purchase.activate.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitClaim = async () => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/public/billing/claim', { token });
      setActivatedPlan(data?.plan || planId);
      setStatus('claimed');
    } catch (e) {
      setError(e.response?.data?.detail || t('purchase.activate.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="card max-w-md w-full py-10 px-6" data-testid="purchase-activate-screen">
        {status === 'loading' && (
          <p className="text-sm text-center text-[var(--text-secondary)]" data-testid="purchase-activate-loading">
            {t('purchase.activate.loading')}
          </p>
        )}

        {status === 'not_found' && (
          <div className="text-center" data-testid="purchase-activate-not-found">
            <WarningCircle size={36} className="mx-auto mb-3" color="var(--accent-pink)" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.activate.notFound')}</p>
          </div>
        )}

        {status === 'expired' && (
          <div className="text-center" data-testid="purchase-activate-expired">
            <WarningCircle size={36} className="mx-auto mb-3" color="var(--accent-pink)" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.activate.expired')}</p>
          </div>
        )}

        {status === 'used' && (
          <div className="text-center" data-testid="purchase-activate-used">
            <WarningCircle size={36} className="mx-auto mb-3" color="var(--accent-pink)" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.activate.used')}</p>
          </div>
        )}

        {status === 'already_claimed' && (
          <div className="text-center" data-testid="purchase-activate-already-claimed">
            <CheckCircle size={36} weight="fill" className="mx-auto mb-3" color="var(--accent-green)" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.activate.alreadyClaimed')}</p>
          </div>
        )}

        {status === 'cancelled' && (
          <div className="text-center" data-testid="purchase-activate-cancelled">
            <WarningCircle size={36} className="mx-auto mb-3" color="var(--accent-pink)" />
            <p className="text-sm text-[var(--text-secondary)]">{t('purchase.activate.cancelled')}</p>
          </div>
        )}

        {status === 'valid' && !accountExists && (
          <form onSubmit={submitNewAccount} data-testid="purchase-activate-new-account-form">
            <h1 className="text-lg font-bold mb-1 text-center">{t('purchase.activate.newAccountTitle')}</h1>
            <p className="text-xs text-[var(--text-muted)] mb-5 text-center">
              {t('purchase.activate.newAccountSubtitle', { plan: planId, email: maskedEmail })}
            </p>
            <label className="block mb-3">
              <span className="text-xs text-[var(--text-muted)] flex items-center gap-1.5 mb-1"><User size={13} /> {t('purchase.activate.namePlaceholder')}</span>
              <input
                className="input-dark w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="purchase-activate-name-input"
                required
              />
            </label>
            <label className="block mb-4">
              <span className="text-xs text-[var(--text-muted)] flex items-center gap-1.5 mb-1"><LockKey size={13} /> {t('purchase.activate.passwordPlaceholder')}</span>
              <input
                type="password"
                className="input-dark w-full"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="purchase-activate-password-input"
                required
              />
            </label>
            {error && <p className="text-xs text-[var(--accent-pink)] mb-3" data-testid="purchase-activate-error">{error}</p>}
            <button type="submit" className="btn-gradient w-full" disabled={submitting} data-testid="purchase-activate-submit-btn">
              {submitting ? t('purchase.activate.creating') : t('purchase.activate.createAccount')}
            </button>
          </form>
        )}

        {status === 'valid' && accountExists && (
          <div className="text-center" data-testid="purchase-activate-existing-account">
            <h1 className="text-lg font-bold mb-2">{t('purchase.activate.existingAccountTitle')}</h1>
            <p className="text-sm text-[var(--text-secondary)] mb-5">
              {t('purchase.activate.existingAccountBody', { email: maskedEmail })}
            </p>
            {error && <p className="text-xs text-[var(--accent-pink)] mb-3" data-testid="purchase-activate-error">{error}</p>}
            {user ? (
              <button className="btn-gradient w-full" onClick={submitClaim} disabled={submitting} data-testid="purchase-activate-claim-btn">
                {submitting ? t('purchase.activate.linking') : t('purchase.activate.confirmLinkButton', { email: user.email })}
              </button>
            ) : (
              <a href="/login" className="btn-ghost inline-block" data-testid="purchase-activate-login-link">
                {t('purchase.activate.goToLogin')}
              </a>
            )}
          </div>
        )}

        {(status === 'activated' || status === 'claimed') && (
          <div className="text-center" data-testid="purchase-activate-success">
            <CheckCircle size={40} weight="fill" className="mx-auto mb-3" color="var(--accent-green)" />
            <p className="text-sm text-[var(--text-secondary)] mb-5">
              {t(status === 'activated' ? 'purchase.activate.success' : 'purchase.activate.claimSuccess', { plan: activatedPlan })}
            </p>
            <a href="/" className="btn-gradient inline-block" data-testid="purchase-activate-continue-btn">
              {t('purchase.activate.continueButton')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
